import { useEffect, useState } from 'react';
import type { NextPage } from 'next';

import { Container } from '@dansk-metal/ui';

import HeroTitle from '@web/components/hero-block/hero-title/hero-title';
import { TabBar } from '@web/components/tab-bar/tab-bar';
import { useQueryParamsTabBar } from '@web/components/tab-bar/use-tab-bar.hook';
import { Task } from '@web/components/task/task';
import { ThemedSkeleton } from '@web/components/themed-skeleton/themed-skeleton';

import TemplateLayout from '@web/layouts/template/template';

import { useAuthenticatedCRMRequestConfig } from '@web/services/crm-api/hooks';
import { TaskDto, useGetMemberTasksByFilter } from '@web/services/crm-api/rest';
import { HeroAlignment } from '@web/services/umbraco/types/basic/Alignment';
import { BackgroundColor } from '@web/services/umbraco/types/basic/Color';
import { UmbracoPage } from '@web/services/umbraco/types/documents/UmbracoDocument';

import styles from './tasks-overview-page.module.scss';

export const tasksOverviewPageContentAlias = 'tasksOverviewPage' as const;

export type TasksOverviewPageData = UmbracoPage<
	typeof tasksOverviewPageContentAlias,
	{
		hero: {
			title: string;
			subtitle?: string;
		};
	}
>;

const TasksOverviewPageTemplate: NextPage<{ pageData: TasksOverviewPageData }> = ({ pageData }) => {
	const { hero } = pageData.properties;
	const tabNames = ['Aktuelle', 'Fuldførte'];
	const { activeTab, setActiveTab } = useQueryParamsTabBar({ tabs: tabNames, queryParam: 'tab' });
	const { memberId: loggedMemberId, requestConfig } = useAuthenticatedCRMRequestConfig();

	const { data, isLoading } = useGetMemberTasksByFilter(
		loggedMemberId,
		{},
		{
			query: {
				enabled: !!loggedMemberId,
			},
			request: requestConfig,
		});

	const [filteredTasksState, setFilteredTasksState] = useState<TaskDto[] | undefined>(data);

	useEffect(() => {
		if (activeTab === 'Aktuelle') {
			const filteredTasks = data?.filter((task) => task.status !== 'completed');
			setFilteredTasksState(filteredTasks);
		} else if (activeTab === 'Fuldførte') {
			const filteredTasks = data?.filter((task) => task.status === 'completed');
			setFilteredTasksState(filteredTasks);
		}
	// Cannot have tasks part of the dependency array, as it will cause an infinite loop since the tasks are being filtered
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeTab, data]);

	return (
		<TemplateLayout>
			{hero.title && (
				<HeroTitle
					title={hero.title}
					subtitle={hero.subtitle}
					alignment={HeroAlignment.Left}
					background={BackgroundColor.White}
				/>
			)}
			<Container>
				<TabBar activeTab={activeTab} tabs={tabNames} setActiveTab={setActiveTab} />
			</Container>
			<div className={styles.tasks_list_section}>
				<Container className={styles.tasks_list_wrapper}>
					{isLoading && (
						<ThemedSkeleton className={styles.loader} count={5} />
					)}
					{(!isLoading && filteredTasksState && (filteredTasksState?.length > 0)) && filteredTasksState.map((task) => (
						<Task key={task.taskId} task={task} />
					))}
					{!isLoading && filteredTasksState?.length === 0 && (
						<div className={styles.empty_state_container}>
							<p>{activeTab === 'Aktuelle' ? 'Du har ingen aktuelle opgaver lige nu - tjek igen senere.' : 'Du har ingen fuldførte opgaver lige nu - tjek igen senere.'}</p>
						</div>
					)}
				</Container>
			</div>
		</TemplateLayout>
	);
};

export default TasksOverviewPageTemplate;
